<template>
  <div id="toolbar" class="d-flex">
    <div class="toolbar-sections d-flex flex-grow-1">
      <div class="toolbar-section logo-section">
        <div v-if="isDemo" class="logo logo-demo">
          <img src="@/assets/svg/Icon_Gradient.svg" />
          <h4>Demo</h4>
        </div>

        <router-link v-else to="/">
          <div class="logo">
            <img src="@/assets/svg/Icon_Gradient.svg" />
          </div>
        </router-link>
      </div>

      <div v-if="renderComponent" class="d-flex flex-grow-1">
        <div
          class="toolbar-section"
          v-for="(toolbarOption, index) in toolbarOptions"
          :key="index"
          :class="{
            'disable-option': editorOpen && toolbarOption.disableWhenEditorOpen,
            'demo-state': isDemo && toolbarOption.demoState
          }"
        >
          <div v-if="showOption(toolbarOption)">
            <div
              v-if="!toolbarOption.dropdownContent"
              :class="toolbarOption.optionClass"
              :tooltip="!toolbarOption.hideTooltip && hideToolbarTitles == true ? toolbarOption.title : ''"
              :ref="'toolbarOption-' + index"
              @click="toolbarClick($event, toolbarOption, index)"
              class="toolbar-option"
              tooltip-placement="bottom"
            >
              <span v-show="!toolbarOption.loading">
                <i class="fa-light" :class="toolbarOption.icon"></i>
              </span>
              <span v-show="toolbarOption.loading">
                <i class="fa-light" :class="toolbarOption.secondIcon"></i>
              </span>
              <span v-if="!toolbarOption.hideTooltipText" class="toolbar-title text-small">{{
                toolbarOption.title
              }}</span>
            </div>
              <demo-data-browser
                v-if="toolbarOption.searchData && showDemoDataBrowser"
                 @close="showDemoDataBrowser = false"
              />
            <dropdown v-if="toolbarOption.dropdownContent" class="toolbar-option" :class="toolbarOption.optionClass">
              <div
                class="name"
                slot="toggle"
                @click="toolbarOption.dropdownToggle(index)"
                :tooltip="!toolbarOption.hideTooltip && hideToolbarTitles == true ? toolbarOption.title : ''"
                tooltip-placement="bottom"
                :ref="'toolbarOption-' + index"
              >
                <span v-show="!toolbarOption.loading">
                  <i class="fa-light" :class="toolbarOption.icon"></i>
                </span>
                <span v-show="toolbarOption.loading">
                  <i class="fa-light" :class="toolbarOption.secondIcon"></i>
                </span>
              </div>
              <div v-if="toolbarOption.dropdownItems" class="dropdown-list">
                <div
                  class="dropdown-item"
                  v-for="(item, index) in toolbarOption.dropdownItems"
                  :key="index"
                  @click="toolbarOption.action(item.value)"
                >
                  <span v-if="item.isDate">
                    {{ item.title | dateFormat("DD.MM.YYYY kl. HH:mm") }}
                  </span>
                  <span v-else>
                    {{ item.title }}
                  </span>
                </div>
              </div>
            </dropdown>


          </div>
        </div>
      </div>
    </div>
    <div v-if="isDemo" class="d-flex align-items-center demo-toolbar">      
      <div class="cta-group">
        <button class="btn" @click="$emit('on-share-template')" :tooltip="$t('demo.toolbar.share-demo')" tooltip-placement="bottom">
          <i class="fa-light fa-arrow-up-from-square"></i>
        </button>
        <button class="btn btn-xs btn-primary btn-invert" @click="$emit('show-hubspot-form')">{{$t('demo.toolbar.book-demo')}}</button>
        <button class="btn btn-xs btn-primary" @click="$emit('show-signup-form')">{{$t('demo.toolbar.signup')}}</button>
      </div>
      

      <div class="demo-settings">
        <dropdown 
					left 
				>
					<div slot="toggle" style="cursor: pointer">
            <i class="fa-light fa-cog"></i>
					</div>
          <div class="dropdown-item" @click.stop="setDemoLocale()">
            <span class="text-muted">{{$t('demo.toolbar.toggle-lang')}}</span>
            <span>{{$store.getters.language == "en" ? $t("profile.pr-lang-da") : $t("profile.pr-lang-en")}}</span>
          </div>
          <div class="dropdown-item" @click="showGuide()">
            {{$t('demo.toolbar.rules')}}
          </div>
				</dropdown>
      </div>
    </div>

    <div v-else-if="$store.state.user" class="d-flex align-items-center current-user">
      <dropdown left class="profile-dropdown" :lockToggle="$store.state.exclusiveDropdown">
        <span class="name btn btn-primary btn-xs btn-flex btn-invert" slot="toggle">
          <i class="fa-light fa-chevron-down"> </i>
          {{ $store.state.user.name }}
        </span>

        <div class="dropdown-item" @click="onChangeAILanguage()">
          <i class="fa-light fa-language"></i>
          <span>
            {{$t('ai-lang')}}
          </span>
          <span class="text-muted text-xs">{{ $store.state.aiTranslationLanguage }}</span>
        </div>

        <hr>

        <router-link to="/" class="dropdown-item">
          <i class="fa-light fa-file"> </i>
          <span>{{ $t("toolbar.templates") }}</span>
        </router-link>

        <router-link to="/settings" class="dropdown-item">
          <i class="fa-light fa-cog"></i>
          <span>{{ $t("toolbar.settings") }}</span>
        </router-link>

        <!-- <toggle-button
          class="comparison-btn dropdown-item"
          :text="'Tooltips'"
          :onToggleButton="toggleTooltips"
          :value="$store.state.showTooltips"
        /> -->

        <a class="dropdown-item" @click="logOut">
          <i class="fa-light fa-sign-out"></i>
          <span>{{ $t("toolbar.logout") }}</span>
        </a>
      </dropdown>
    </div>

    <template-extra-variables-popover v-if="showExtraVariablesPopover" @close="showExtraVariablesPopover = false" />
    <template-methods-popover v-if="showMethodsPopover" @close="showMethodsPopover = false" />
    <template-stats-popover
      v-if="showStatsPopover"
      @close="showStatsPopover = false"
      :allText="allText"
      :selectedDataset="selectedDataset"
    />
    <template-data-popover
      v-if="showTemplateDataPopover"
      @close="showTemplateDataPopover = false"
      :lockTemplateDataSearch="lockTemplateDataSearch"
    />
    <template-syns-popover
      v-if="showGlobalSynsPopover"
      @close="showGlobalSynsPopover = false"
      :template="$store.state.template"
    />
 
  </div>
</template>

<script>
let getSavedTooltipSettings = () => {
  return JSON.parse(localStorage.getItem("toggleShowTooltips"));
};

import EventBus from "@/assets/js/eventBus.js";
import { editorMixin } from "@/assets/js/editorMixin.js";
import ToggleButton from "@/components/ToggleButton";
import TemplateExtraVariablesPopover from "@/components/TemplateExtraVariablesPopover";
import TemplateMethodsPopover from "@/components/TemplateMethodsPopover";
import TemplateDataPopover from "@/components/TemplateDataPopover";
import TemplateStatsPopover from "@/components/TemplateStatsPopover";
import TemplateSynsPopover from "@/components/TemplateSynsPopover";
import LeaveEditor from "@/components/modals/LeaveEditor";
import router from "@/router";
import DemoDataBrowser from  "@/components/DemoDataBrowser"
import AiLanguageModal from "@/components/modals/AiLanguageModal";
import DemoRuleseModal from "@/components/modals/DemoRulesModal";
export default {
  mixins: [editorMixin],
  data() {
    return {
      windowWidth: window.innerHeight,
      showExtraVariablesPopover: false,
      showMethodsPopover: false,
      showTemplateDataPopover: false,
      showStatsPopover: false,
      showGlobalSynsPopover: false,
      randomized: false,
      refreshed: false,
      searching: false,
      editorOpen: false,
      allText: [],
      lockTemplateDataSearch: false,
      comparisonView: false,
      desiredLayoutColumns: "column-two",
      desiredLayoutRows: "row-one",
      tooltipSettings: getSavedTooltipSettings(),
      // templateBackups: this.$store.state.templateBackups,
      searchingLog: false,
      renderComponent: true,
      showDemoDataBrowser: false,
      // demoLanguage: this.$store.getters.language,
      toolbarOptionsStandard: [
        {
          title: this.$t("toolbar.change-data"),
          action: () => {
            this.useDemoDataBrowser ? (this.showDemoDataBrowser = !this.showDemoDataBrowser) : (this.showTemplateDataPopover = true);
          },
          icon: "fa-search",
          hideTooltip: true,
          hideTooltipText: false,
          searchData: true,
          disableWhenEditorOpen: !this.isDemo
        },
        {
          title: this.$t("toolbar.randomize"),
          action: (index) => {
            this.onRandomSeed(index);
          },
          icon: "fa-random",
          secondIcon: "fa-circle-notch fa-spin",
          hideTooltip: false,
          hideTooltipText: true,
          loading: false,
          disableWhenEditorOpen: true
        },
        {
          title: this.$t("toolbar.stats"),
          action: () => {
            this.showStatsPopover = true;
          },
          icon: "fa-chart-pie",
          hideTooltip: false,
          hideTooltipText: true,
          disableWhenEditorOpen: true
        },
        {
          title: this.$t("toolbar.extra-vars"),
          action: () => {
            this.showExtraVariablesPopover = true;
          },
          icon: "fa-book",
          hideTooltip: false,
          hideTooltipText: true,
          disableWhenEditorOpen: true,
          disableIfDemo: true,
          ctaIfDemo: true,
          ctaText: "extraVars"
        },
        {
          title: this.$t("template-syns.title"),
          action: () => {
            this.showGlobalSynsPopover = true;
          },
          icon: "fa-globe",
          hideTooltip: false,
          hideTooltipText: true,
          disableIfDemo: true,
          ctaIfDemo: true,
          ctaText: "globalSyns"
        },
        {
          title: "Log",
          action: (backup) => {
            this.getBackupInfo(backup);
          },
          icon: "fa-history",
          hideTooltip: false,
          hideTooltipText: true,
          dropdownContent: true,
          dropdownToggle: (index) => {
            this.logBtnClicked(index);
          },
          dropdownItems: [],
          disableWhenEditorOpen: true,
          hideIfDemo: true
        }
      ],

      toolbarOptionsComparisonView: [
        {
          title: this.$t("multiview.add-preview"),
          action: () => {
            this.addSinglePreview();
          },
          icon: "fa-plus",
          hideTooltip: false,
          hideTooltipText: true
        },
        {
          title: this.$t("multiview.refresh"),
          action: () => {
            this.refreshAll();
          },
          icon: "fa-sync-alt",
          hideTooltip: false,
          hideTooltipText: true
        },
        {
          title: this.$t("multiview.randomize-all"),
          action: (index) => {
            this.randomizeAll(index);
          },
          icon: "fa-random",
          secondIcon: "fa-circle-notch fa-spin",
          hideTooltip: false,
          hideTooltipText: true,
          loading: false
        },
        {
          title: this.$t("multiview.columns"),
          action: (res) => {
            this.desiredLayoutColumns = res;
          },
          icon: "fa-align-justify",
          optionClass: "columns-option",
          hideTooltip: false,
          hideTooltipText: true,
          dropdownContent: true,
          dropdownToggle: () => {},
          dropdownItems: [
            {
              title: "1 " + this.$t("multiview.column"),
              value: "column-one"
            },
            {
              title: "2 " + this.$t("multiview.columns"),
              value: "column-two"
            },
            {
              title: "3 " + this.$t("multiview.columns"),
              value: "column-three"
            }
          ]
        },
        {
          title: this.$t("multiview.rows"),
          action: (res) => {
            this.desiredLayoutRows = res;
          },
          icon: "fa-align-justify",
          hideTooltip: false,
          hideTooltipText: true,
          dropdownContent: true,
          dropdownToggle: () => {},
          dropdownItems: [
            {
              title: "1 " + this.$t("multiview.row"),
              value: "row-one"
            },
            {
              title: "2 " + this.$t("multiview.rows"),
              value: "row-two"
            },
            {
              title: "3 " + this.$t("multiview.rows"),
              value: "row-three"
            }
          ]
        }
      ]
    };
  },
  methods: {
    onChangeAILanguage(){
      this.$modal.show(
        AiLanguageModal,
        {},
        {
          height: "auto",
          width: 350
        }
      );
    },
    showGuide() {
      this.$modal.show(DemoRuleseModal,{},   { width: 500, height: "auto", adaptive: true })
    },
     setDemoLocale() {
      const locale = this.$store.getters.language == "en" ? "da" : "en"
      localStorage.setItem("language-storage", locale);
      this.savedLanguage = locale;
      this.$i18n.locale = locale;
      this.$emit("rerender-toolbar")
    },
    showOption(toolbarOption) {
      if (this.isDemo && toolbarOption.hideIfDemo) return false;
      else return true;
    },

    toolbarClick(e, toolbarOption, index) {
      if (toolbarOption.ctaIfDemo) {
        this.handleAction(e, toolbarOption.ctaText, () => {
          toolbarOption.action(index);
        });
      } else {
        toolbarOption.action(index);
      }
    },

    templateBackupsList() {
      return (
        this.$store.state.templateBackups.map((b) => ({
          title: new Date(b.time),
          value: b,
          isDate: true
        })) || [{ title: "None" }]
      );
    },
    forceRenderer() {
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
    },
    logBtnClicked(index) {
      if (this.templateBackups.length == 0) {
        this.getBackups();
      }
      this.$set(this.toolbarOptions[index], "dropdownItems", this.templateBackupsList());
    },
    getBackupInfo(backup) {
      const originalTemplateData = { ...this.$store.state.template };
      // const originalTemplateData2 = this.$store.state.template.clone()
      this.$store
        .dispatch("getBackupInfo", {
          templateId: this.$store.state.template.metadata.id,
          backupId: backup.id
        })
        .then((info) => {
          this.$set(this.$store.state.template, "sections", info.body.sections);
          this.$store.commit("setTemplateData", {
            template: this.$store.state.template,
            variables: this.$store.state.templateVariables
          });

          this.$store
            .dispatch("getTemplatePreview", {
              template: this.$store.state.template,
              variables: this.$store.state.templateVariables
            })
            .then((preview) => {
              this.$store.state.showingTemplateBackupInPreview = backup;
              // this.$store.state.templatePreview = preview;
              this.$store.state.originalTemplateData = originalTemplateData;

              this.$store.commit("setTemplatePreview", preview);
            })
            .catch((e) => {
              this.handleError(backup);
              this.$store.commit("setTemplateData", {
                template: originalTemplateData,
                variables: this.$store.state.templateVariables
              });
            });
        })
        .catch((e) => {
          this.handleError(backup);
        });
    },
    handleError(backup) {
      // If an error occurs, let the user know and try to get the backup again.
    },
    getBackups() {
      this.searchingLog = true;
      this.$store
        .dispatch("getTemplateBackups", this.$store.state.template.metadata.id)
        .then((backups) => {
          // this.templateBackups = backups.body
          this.searchingLog = false;
        })
        .catch((e) => {
          this.searchingLog = false;
        });
    },
    addSinglePreview() {
      EventBus.$emit("new-preview");
    },
    toggleTooltips() {
      this.$store.commit("toggleShowTooltips");
    },
    randomizeAll(index) {
      this.$set(this.toolbarOptions[index], "loading", true);
      this.$store.state.templateMultiplePreview.forEach((preview) => {
        this.randomized = true;
        preview.loading = true;
        router.currentRoute.query.seed = Math.floor(Math.random() * 10000) + 1;
        this.$store
          .dispatch("getTemplatePreview", {
            template: this.$store.state.template,
            variables: preview.variables
          })
          .then((newPreview) => {
            preview.preview = newPreview;
            preview.loading = false;
            this.randomized = false;
            this.$set(this.toolbarOptions[index], "loading", false);
          });
      });
    },
    refreshAll() {
      router.currentRoute.query.seed = 1;
      this.$store.dispatch("refreshAllPreviews");
    },
    onRandomSeed(index) {
      this.$set(this.toolbarOptions[index], "loading", true);
      router.currentRoute.query.seed = Math.floor(Math.random() * 10000) + 1;
      this.$store
        .dispatch("getTemplatePreview", {
          template: this.$store.state.template,
          variables: this.$store.state.templateVariables
        })
        .then((preview) => {
          // this.$store.state.templatePreview = preview;
          this.$store.commit("setTemplatePreview", preview);
          this.$set(this.toolbarOptions[index], "loading", false);
          this.randomized = false;
        })
        .catch((e) => {
          this.$set(this.toolbarOptions[index], "loading", false);
          this.randomized = false;
        });
      this.randomized = true;
    },
    logOut() {
      this.$modal.show(
        LeaveEditor,
        {
          thirdOption: this.$store.state.unsavedChanges ? true : false,
          title: this.$t("logout.title"),
          description: this.$store.state.unsavedChanges
            ? this.$t("logout.desc") + this.$t("logout.desc-extra")
            : this.$t("logout.desc"),
          confirmButtonText: this.$t("logout.confirm"),
          confirmBtnPrimaryColor: this.$store.state.unsavedChanges ? false : true,
          thirdColorBtnPrimary: true,
          thirdOptionText: "Save & logout",
          onThirdOptionClick: (result) => {
            this.$store.dispatch("saveTemplateChanges").then(
              () => {
                this.$store.dispatch("logOut").then(() => {
                  this.$router.push("login");
                  location.reload();
                });
              },
              () => {}
            );
          },
          onConfirm: (result) => {
            this.$store.dispatch("logOut").then(() => {
              this.$router.push("login");
              location.reload();
            });
          },
          onCancel: (result) => {}
        },
        { width: 450, height: "auto", adaptive: true }
      );
    },
    disableBtn() {
      this.editorOpen = true;
    },
    enableBtn() {
      this.editorOpen = false;
    },
    updateData(sectionText) {
      this.allText = sectionText;
    },
    lockDataBrowser() {
      this.lockTemplateDataSearch = true;
    },
    unlockDataBrowser() {
      this.lockTemplateDataSearch = false;
    },
    enableComparisonView() {
      this.comparisonView = true;
    },
    disableComparisonView() {
      this.comparisonView = false;
    }
  },
  computed: {
    toolbarOptions() {
      return this.comparisonView ? this.toolbarOptionsComparisonView : this.toolbarOptionsStandard;
    },
    selectedDataset() {
      return this.$store.state.selectedDataset;
    },
    eventBus() {
      return EventBus;
    },
    templateBackups() {
      return this.$store.state.templateBackups;
    },
    templateVariables() {
      return this.$store.state.templateVariables;
    },
    // selectedDataset(){return this.$store.state.selectedDataset },
    templatePreview() {
      return this.$store.state.templatePreview;
    },
    templateMultipleDatasets() {
      return this.$store.state.templateMultipleDatasets;
    },
    templateMultiplePreview() {
      return this.$store.state.templateMultiplePreview;
    },
    hideToolbarTitles() {
      //   return this.windowWidth < 1130;
      return true;
    },
    isDemo() {
      return this.$store.state.editorIsDemoVersion;
    },
    useDemoDataBrowser(){
      // return this.isDemo && this.$store.state.userDevice != "desktop"
      return this.isDemo
    }
  },
  watch: {
    desiredLayoutColumns: function(oldVal, newVal) {
      EventBus.$emit("desired-layout-changed-columns", this.desiredLayoutColumns);
    },
    desiredLayoutRows: function(oldVal, newVal) {
      EventBus.$emit("desired-layout-changed-rows", this.desiredLayoutRows);
    },
    templateVariables: function() {
      //In case something happens in splitscreen and templatevars are gone, reset to example data and main view
      if (this.$store.state.templateVariables == undefined) {
        this.$store.state.templatePreview = this.$store.state.templateMultiplePreview[0].preview;
        this.$store.state.selectedDataset = this.$store.state.templateMultiplePreview[0].selectedDataset;
        this.$store.state.templateVariables = this.$store.state.templateMultiplePreview[0].variables;
      }
    },
    comparisonView: function() {
      this.forceRenderer();
    }
  },
  created() {
    EventBus.$once("text_stats", (sectionText) => {
      this.updateData(sectionText);
    }),
      EventBus.$on("lock-databrowser", this.lockDataBrowser);
    if (this.tooltipSettings == null) {
      this.$store.commit("toggleShowTooltips");
    } else {
      this.$store.state.showTooltips = this.tooltipSettings;
    }
  },
  mounted() {
    EventBus.$on("unlock-databrowser", this.unlockDataBrowser);
    EventBus.$on("comparison_view_on", this.enableComparisonView);
    EventBus.$on("comparison_view_off", this.disableComparisonView);
    EventBus.$on("close_panel", this.disableBtn);
    EventBus.$on("open_panel", this.enableBtn);
    EventBus.$on("update-multi-view", this.refreshAll);

    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  destroyed() {
    EventBus.$off("lock-databrowser");
  },
  components: {
    TemplateDataPopover,
    TemplateStatsPopover,
    TemplateExtraVariablesPopover,
    TemplateMethodsPopover,
    ToggleButton,
    TemplateSynsPopover,
    DemoDataBrowser
  }
};
</script>

<style lang="less">
#toolbar {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
  height: 55px;
  min-height: 55px;
  z-index: 10;

  .logo {
    width: 85px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg,
    img {
      filter: brightness(0%);
      width: 25px;
    }
    &.logo-demo {
      display: flex;
      flex-direction: column;
      h4 {
        font-size: 12px;
        margin-bottom: 0;
        color: #000;
      }
      img {
        filter: brightness(0%);
        width: 20px;
      }
    }
  }

  .toolbar-section {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    &.logo-section {
      border-right: 1px solid #d8d8d8;
    }
  }

  .disabled-toolbar-btn {
    pointer-events: none;
    color: grey;
  }
  .randomize-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toolbar-option {
    padding: 0 24px;
    flex-direction: row;
    cursor: pointer;
    white-space: nowrap;

    .toolbar-title {
      margin-left: 4px;
    }
  }
  .disable-option {
    pointer-events: none;
    color: grey;
  }
  .columns-option {
    .name span > svg {
      transform: rotate(90deg);
    }
  }

  .menu-toggle {
    padding: 0 25px;
    flex-direction: row;
    transition: 0.3s;
    cursor: pointer;
    white-space: nowrap;

    .fa-random,
    .fa-search,
    .fa-chart-pie,
    .fa-chart-bar,
    .fa-book,
    .fa-atlas,
    .fa-plus,
    .fa-spin,
    .fa-globe,
    svg {
      width: 14px;
      margin-right: 2px;
    }

    &:hover {
      color: var(--text-grey-primary);
    }
    .toolbar-icon {
      position: relative;
    }

    & > span:not(.change-data):not(.toggle) {
      display: none;
    }
  }

  .current-user {
    .name {
      cursor: pointer;
      margin-right: 10px;
      font-size: 12px;
      font-weight: 500;
    }
    padding: 0 20px;
  }

  .turn-anim > svg.fa-random,
  .turn-anim > svg.fa-sync-alt {
    animation: flash 0.4s infinite linear;
    transform-origin: 50% 50%;
  }
  .turn-anim {
    transition: 0.3s;
  }

  .search-anim > svg.fa-search,
  .search-anim > svg.fa-chart-pie,
  .search-anim > svg.fa-chart-bar,
  .search-anim > svg.fa-book,
  .search-anim > svg.fa-atlas {
    animation: search 0.4s ease-out;
    transform-origin: 37% 50%;
  }

  @keyframes search {
    0% {
      opacity: 1;
      transform: scale(1);
    }

    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  @keyframes flash {
    from {
      opacity: 0.8;
      transform: rotate(0deg) scale(0.8);
    }
    to {
      opacity: 0.8;
      transform: rotate(-360deg) scale(0.8);
    }
  }

  .selected-layout {
    font-weight: bold;
  }

  .profile-dropdown {
    // z-index: 100;
    z-index: 2;
    .dropdown-item {
      width: 220px;
      color: #3d464a;
      display: flex;
      align-items: center;
      span {
        margin-left: 10px;
      }
      .slider {
        margin-left: -3px;
      }
      label {
        margin-left: 5px;
      }
      svg {
        font-size: 16px;
      }
    }
  }
  .backup-list {
    display: flex;
    flex-direction: row;
  }

  .demo-toolbar{
    .demo-settings{
      margin-right: 12px;
    }
    .cta-group {
      display: flex;
      align-items: center;
      gap: 4px;
      .btn-primary {
        margin-right: 8px;
      }
      svg {
        color: var(--flame);
        font-size: 16px;
      }
    }
  }
}
</style>
